import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { type Theme } from '@mui/material/styles';
import { ErrorMessage, Formik } from 'formik';
import api from '@/services/api';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { changePasswordSchema } from '@/validations/passwordScheme';
import Card from '@/components/Card';
import Stack from '@mui/material/Stack';
import { Check, Eye, EyeOff, KeyRoundIcon, UserPlus2 } from 'lucide-react';
import AlertDialog from '../AlertDialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import TextField from '@components/Input/TextField';
import {
  HeaderStack,
  PasswordFieldWrapper,
  PasswordLabelStack,
  PasswordInputRowEndAligned,
  ActionButtonStack,
  ChangePasswordModal,
  PasswordLabelTypography,
  CancelButton,
  SubmitButton,
} from './styles';
interface RegisterUserProps {
  open: boolean;
  handleClose: () => void;
  adminOperation?: boolean;
  userId?: number;
}
function ChangePassword({
  handleClose,
  open,
  adminOperation = false,
  userId,
}: RegisterUserProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initialValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);
  const [value, setValue] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [currentPassword, setCurrentPassword] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState(false);

  const confimDialogBox = (values: {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => {
    setPasswordValues(values);
    setOpenDialog(true);
  };

  useEffect(() => {
    if (value) {
      handleChangePassword(passwordValues);
      setValue(false);
    }
  }, [value]);

  const handleChangePassword = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => {
    try {
      const userString = localStorage.getItem('user');
      const targetId = userId ?? (userString && JSON.parse(userString).id);
      if (!targetId) {
        enqueueSnackbar(t('config.users.changePassword.userNotFound'), {
          variant: 'error',
        });
        return;
      }
      const response = await api.patch(
        `/users/${targetId}`,
        adminOperation
          ? { newPassword: values.newPassword }
          : {
              oldPassword: values.oldPassword,
              newPassword: values.newPassword,
            }
      );

      if (response.status === 204) {
        enqueueSnackbar(t('config.users.changePassword.successMessage'), {
          variant: 'success',
        });
        handleClose();
      } else {
        handleClose();
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          enqueueSnackbar(
            error.response.data?.detail ||
              t('config.users.changePassword.errorMessage'),
            { variant: 'error' }
          );
        }
      }
    }
  };

  return (
    <div>
      <ChangePasswordModal open={open} onClose={handleClose}>
        <Card sx={{ alignItems: 'center', width: isSm ? '90vw' : 'auto' }}>
          <Formik
            initialValues={initialValues}
            validationSchema={changePasswordSchema(adminOperation)}
            onSubmit={confimDialogBox}
          >
            {({ values, handleChange, submitForm, errors, touched }) => (
              <>
                <Stack>
                  <HeaderStack>
                    <UserPlus2 strokeWidth={2.5} />
                    <Typography variant="h3">
                      {t('config.users.changePassword.title')}
                    </Typography>
                  </HeaderStack>
                  <PasswordFieldWrapper>
                    {/** Current user password  */}
                    {!adminOperation && (
                      <PasswordInputRowEndAligned>
                        <PasswordLabelStack>
                          <KeyRoundIcon size={20} />
                          <PasswordLabelTypography variant="caption">
                            {t('config.users.changePassword.currentPassword')}
                          </PasswordLabelTypography>
                        </PasswordLabelStack>

                        <TextField
                          type={currentPassword ? 'text' : 'password'}
                          value={values.oldPassword}
                          onChange={handleChange}
                          name="oldPassword"
                          id="oldPassword"
                          endIcon={
                            currentPassword ? (
                              <EyeOff
                                size={18}
                                cursor="pointer"
                                onClick={() => setCurrentPassword(false)}
                              />
                            ) : (
                              <Eye
                                size={18}
                                cursor="pointer"
                                onClick={() => setCurrentPassword(true)}
                              />
                            )
                          }
                          error={
                            touched.oldPassword && Boolean(errors.oldPassword)
                          }
                          helperText={<ErrorMessage name="oldPassword" />}
                        />
                      </PasswordInputRowEndAligned>
                    )}

                    {/** New user password  */}
                    <PasswordInputRowEndAligned>
                      <PasswordLabelStack>
                        <KeyRoundIcon
                          size={20}
                          color={theme.palette.white['06']}
                        />
                        <PasswordLabelTypography variant="caption">
                          {t('config.users.changePassword.newPassword')}
                        </PasswordLabelTypography>
                      </PasswordLabelStack>

                      <TextField
                        type={newPassword ? 'text' : 'password'}
                        value={values.newPassword}
                        onChange={handleChange}
                        name="newPassword"
                        id="newPassword"
                        error={
                          touched.newPassword && Boolean(errors.newPassword)
                        }
                        helperText={<ErrorMessage name="newPassword" />}
                        endIcon={
                          newPassword ? (
                            <EyeOff
                              size={18}
                              cursor="pointer"
                              onClick={() => setNewPassword(false)}
                            />
                          ) : (
                            <Eye
                              size={18}
                              cursor="pointer"
                              onClick={() => setNewPassword(true)}
                            />
                          )
                        }
                      />
                    </PasswordInputRowEndAligned>

                    {/** New user password */}
                    <PasswordInputRowEndAligned>
                      <PasswordLabelStack>
                        <KeyRoundIcon
                          size={20}
                          color={theme.palette.white['06']}
                        />
                        <PasswordLabelTypography variant="caption">
                          {t('config.users.changePassword.confirmNewPassword')}
                        </PasswordLabelTypography>
                      </PasswordLabelStack>

                      <TextField
                        type={confirmNewPassword ? 'text' : 'password'}
                        value={values.confirmNewPassword}
                        onChange={handleChange}
                        name="confirmNewPassword"
                        id="confirmNewPassword"
                        error={
                          touched.confirmNewPassword &&
                          Boolean(errors.confirmNewPassword)
                        }
                        helperText={<ErrorMessage name="confirmNewPassword" />}
                        endIcon={
                          confirmNewPassword ? (
                            <EyeOff
                              size={18}
                              cursor="pointer"
                              onClick={() => setConfirmNewPassword(false)}
                            />
                          ) : (
                            <Eye
                              size={18}
                              cursor="pointer"
                              onClick={() => setConfirmNewPassword(true)}
                            />
                          )
                        }
                      />
                    </PasswordInputRowEndAligned>

                    <ActionButtonStack>
                      <CancelButton variant="contained" onClick={handleClose}>
                        {t('config.variables.createUser.cancel')}
                      </CancelButton>
                      <SubmitButton
                        variant="contained"
                        type="submit"
                        onClick={submitForm}
                      >
                        <Check size={18} />
                        {t('config.users.changePassword.edit')}
                      </SubmitButton>
                    </ActionButtonStack>
                  </PasswordFieldWrapper>
                </Stack>
              </>
            )}
          </Formik>
        </Card>
      </ChangePasswordModal>
      <AlertDialog
        open={openDialog}
        setOpen={setOpenDialog}
        title={t('config.users.changePassword.confirmDialogTitle')}
        content={t('config.users.changePassword.confirmDialogContent')}
        setValue={setValue}
      />
    </div>
  );
}

export default ChangePassword;
